import React, { useState } from "react";
import { useSpamGuard } from "@/lib/spamGuard";
import { Send } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";

const CERT = [
  { v: "completed", label: "Completed Aviana certification", dateLabel: "Date completed", ph: "e.g. October 2024" },
  { v: "in_progress", label: "Currently enrolled or in progress", dateLabel: "Expected completion date", ph: "e.g. Spring 2026" },
  { v: "planning", label: "Planning to enroll", dateLabel: "Intended timeline", ph: "e.g. Within the next year" },
];
const CONTEXTS = ["Studio", "Private", "Retreat", "Other"];
const EMPTY = {
  full_name: "", email: "", certification_status: "", certification_date: "", years_teaching: "",
  teaching_context: [], teaching_context_other: "", studio_owner: "", classes_per_month: "",
  drawn_to_lineage: "", bring_training_where: "", hope_to_contribute: "", has_insurance: "",
  tarot_course: "", anything_else: "",
};

const inputCls = "w-full bg-white/80 border border-aviana-border rounded-xl px-4 py-3 text-[15px] text-aviana-ink outline-none focus:border-aviana-plum transition-colors";

function Q({ label, children, hint }) {
  return (
    <div>
      <p className="block text-[11px] tracking-[0.2em] uppercase text-aviana-plum mb-2.5">{label}</p>
      {children}
      {hint && <p className="text-[12px] text-aviana-muted mt-1.5">{hint}</p>}
    </div>
  );
}

function Choice({ name, options, value, onChange }) {
  return (
    <div className="flex flex-wrap gap-2.5">
      {options.map(([v, label]) => (
        <label key={v} className={`cursor-pointer px-4 py-2.5 rounded-full border text-[13px] transition-colors ${value === v ? "bg-aviana-plum text-aviana-cream border-aviana-plum" : "bg-white/70 border-aviana-border text-aviana-inkSoft hover:border-aviana-plum"}`}
          data-testid={`apply-${name}-${v}`}>
          <input type="radio" name={name} value={v} checked={value === v} onChange={() => onChange(v)} className="sr-only" />
          {label}
        </label>
      ))}
    </div>
  );
}

function Thanks() {
  return (
    <div className="text-center py-16" data-testid="apply-thanks">
      <span className="ornament-flower mx-auto block" />
      <h3 className="font-display text-4xl md:text-5xl text-aviana-ink mt-8">Thank you, <em className="italic font-serif gold-text">truly.</em></h3>
      <p className="mt-6 text-aviana-muted font-serif italic text-lg leading-[1.8] max-w-xl mx-auto">
        Your application is with Holly. She reads every one personally and will be in touch soon. A confirmation is on its way to your inbox.
      </p>
    </div>
  );
}

export default function TrainerApplicationForm() {
  const { honeypot, guard } = useSpamGuard();
  const [f, setF] = useState(EMPTY);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  const toggleCtx = (c) => set("teaching_context", f.teaching_context.includes(c) ? f.teaching_context.filter((x) => x !== c) : [...f.teaching_context, c]);
  const cert = CERT.find((c) => c.v === f.certification_status);

  const submit = async (e) => {
    e.preventDefault();
    const missing = ["certification_status", "studio_owner", "has_insurance", "tarot_course"].filter((k) => !f[k]);
    if (missing.length || f.teaching_context.length === 0) {
      toast.error("Please answer every question marked with a choice.");
      return;
    }
    setBusy(true);
    try {
      await api.post("/trainer-applications", { ...f, ...guard() });
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not send your application. Please try again.");
    } finally { setBusy(false); }
  };

  if (done) return <Thanks />;
  return (
    <form onSubmit={submit} className="space-y-9" data-testid="trainer-application-form">
            {honeypot}
      <div className="grid md:grid-cols-2 gap-6">
        <Q label="Full name"><input required value={f.full_name} onChange={(e) => set("full_name", e.target.value)} className={inputCls} data-testid="apply-full-name" /></Q>
        <Q label="Email"><input required type="email" value={f.email} onChange={(e) => set("email", e.target.value)} className={inputCls} data-testid="apply-email" /></Q>
      </div>
      <Q label="Certification status">
        <Choice name="cert" value={f.certification_status} onChange={(v) => set("certification_status", v)} options={CERT.map((c) => [c.v, c.label])} />
        {cert && (
          <div className="mt-4 max-w-sm">
            <p className="text-[12px] text-aviana-muted mb-1.5">{cert.dateLabel}</p>
            <input required value={f.certification_date} onChange={(e) => set("certification_date", e.target.value)} placeholder={cert.ph} className={inputCls} data-testid="apply-cert-date" />
          </div>
        )}
      </Q>
      <div className="grid md:grid-cols-2 gap-6">
        <Q label="How long have you been teaching aerial yoga?"><input required value={f.years_teaching} onChange={(e) => set("years_teaching", e.target.value)} placeholder="e.g. 3 years" className={inputCls} data-testid="apply-years" /></Q>
        <Q label="Approximate classes taught per month"><input required value={f.classes_per_month} onChange={(e) => set("classes_per_month", e.target.value)} placeholder="e.g. 12" className={inputCls} data-testid="apply-classes" /></Q>
      </div>
      <Q label="Current teaching context" hint="Choose all that apply.">
        <div className="flex flex-wrap gap-2.5">
          {CONTEXTS.map((c) => (
            <button type="button" key={c} onClick={() => toggleCtx(c)} data-testid={`apply-context-${c.toLowerCase()}`}
              className={`px-4 py-2.5 rounded-full border text-[13px] transition-colors ${f.teaching_context.includes(c) ? "bg-aviana-plum text-aviana-cream border-aviana-plum" : "bg-white/70 border-aviana-border text-aviana-inkSoft hover:border-aviana-plum"}`}>
              {c}
            </button>
          ))}
        </div>
        {f.teaching_context.includes("Other") && (
          <input value={f.teaching_context_other} onChange={(e) => set("teaching_context_other", e.target.value)} placeholder="Tell us more" className={`${inputCls} mt-3 max-w-md`} data-testid="apply-context-other-text" />
        )}
      </Q>
      <Q label="Are you a studio owner?"><Choice name="studio" value={f.studio_owner} onChange={(v) => set("studio_owner", v)} options={[["yes", "Yes"], ["no", "No"]]} /></Q>
      <Q label="What draws you to the Aviana lineage specifically?"><textarea required rows={4} value={f.drawn_to_lineage} onChange={(e) => set("drawn_to_lineage", e.target.value)} className={inputCls} data-testid="apply-drawn" /></Q>
      <Q label="Where would you want to bring a teacher training?" hint="City, community, setting."><textarea required rows={3} value={f.bring_training_where} onChange={(e) => set("bring_training_where", e.target.value)} className={inputCls} data-testid="apply-where" /></Q>
      <Q label="What do you hope to contribute to the Aviana circle?"><textarea required rows={4} value={f.hope_to_contribute} onChange={(e) => set("hope_to_contribute", e.target.value)} className={inputCls} data-testid="apply-contribute" /></Q>
      <div className="grid md:grid-cols-2 gap-6">
        <Q label="Do you currently carry liability insurance?"><Choice name="insurance" value={f.has_insurance} onChange={(v) => set("has_insurance", v)} options={[["yes", "Yes"], ["no", "No"]]} /></Q>
        <Q label="Open to the Mythical Goddess Tarot course by Katherine Skaggs?">
          <Choice name="tarot" value={f.tarot_course} onChange={(v) => set("tarot_course", v)} options={[["yes", "Yes"], ["no", "No"], ["completed", "Already completed"]]} />
        </Q>
      </div>
      <Q label="Anything else you'd like Holly to know?"><textarea rows={4} value={f.anything_else} onChange={(e) => set("anything_else", e.target.value)} className={inputCls} data-testid="apply-else" /></Q>
      <button type="submit" disabled={busy} className="btn-gold !px-9 !py-4 !text-[13px]" data-testid="apply-submit">
        {busy ? "Sending…" : <>Send my application <Send size={14} /></>}
      </button>
    </form>
  );
}
